/**
 * NodeInfoPopup — мини-модалка с описанием бизнеса экосистемы.
 *
 * Появляется при тапе на маленький кружок ноды (TM, VPN, PF, ST, CS,
 * AI, AD) на orbit. Закрывается тапом outside, кнопкой ×, ESC.
 *
 * Сверстана как glass-bottom-sheet (slide-up), компактная — ровно
 * чтобы поместить иконку, название и 2-3 строки описания. Не растёт.
 */
import { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { BusinessInfo } from '@/data/businesses';
import { useUISounds } from '@/hooks/useUISounds';

interface NodeInfoPopupProps {
  business: BusinessInfo | null;
  onClose: () => void;
}

export function NodeInfoPopup({ business, onClose }: NodeInfoPopupProps) {
  const open = business !== null;
  const sounds = useUISounds();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && business && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={() => { sounds.bellClose(); onClose(); }}
        >
          <div className="absolute inset-0 bg-black/70" />

          <motion.div
            className="relative w-full max-w-xs rounded-[26px] overflow-hidden"
            style={{
              // Solid фон вместо glass-fill (полупрозрачный +
              // backdrop-filter) — рендерится мгновенно.
              background:
                'linear-gradient(180deg, rgba(28,28,42,0.98) 0%, rgba(18,18,30,0.98) 100%)',
              border: '1px solid var(--glass-stroke-strong)',
              boxShadow:
                'var(--glass-shadow), inset 0 1.5px 0 0 var(--glass-rim-light), inset 0 -1px 0 0 var(--glass-rim-dark)',
            }}
            initial={{ y: 20, opacity: 0, scale: 0.92 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 flex items-start gap-3">
              {/* Big coloured emoji avatar */}
              <div
                className="h-12 w-12 flex-shrink-0 rounded-full flex items-center justify-center text-[22px]"
                style={{
                  background: `hsl(${business.hue} 70% 60% / 0.22)`,
                  border: `1.5px solid hsl(${business.hue} 80% 65%)`,
                  boxShadow: `0 0 28px -4px hsl(${business.hue} 80% 60% / 0.6)`,
                }}
              >
                {business.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                  Источник транзакций
                </p>
                <h3 className="font-display text-[17px] font-extrabold text-[var(--text-primary)] tracking-tight">
                  {business.name}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => { sounds.bellClose(); onClose(); }}
                data-no-sound="true"
                className="lg-pill h-7 w-7 flex-shrink-0 flex items-center justify-center text-[var(--text-secondary)] text-[14px] leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-5 pb-4">
              <p className="text-[12px] leading-[1.55] text-[var(--text-secondary)]">
                {business.description}
              </p>
            </div>

            <div className="px-5 py-3 border-t border-[var(--glass-stroke)] flex items-center justify-between text-[9px] uppercase tracking-[0.16em] font-mono text-[var(--text-disabled)]">
              <span>{business.short}</span>
              <span>1 of 7 nodes</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
